/**
 * Terminal API Service
 *
 * Renderer-side wrappers for terminal IPC calls.
 */

import type {
  TerminalCreateRequest,
  TerminalCreateResult,
  TerminalWriteRequest,
  TerminalWriteResult,
  TerminalResizeRequest,
  TerminalResizeResult,
  TerminalCloseRequest,
  TerminalCloseResult,
  TerminalOutputEvent,
  TerminalExitEvent,
} from '../../../types/terminal';

/**
 * Get the terminal API from electronAPI
 */
function getTerminalApi() {
  const api = window.electronAPI?.terminal;
  if (!api) {
    throw new Error('Electron Terminal API is not available');
  }
  return api;
}

/**
 * Create a new terminal session
 */
export async function createTerminalSession(
  request: TerminalCreateRequest
): Promise<TerminalCreateResult> {
  return getTerminalApi().create(request);
}

/**
 * Write data to a terminal session
 */
export async function writeToTerminal(
  request: TerminalWriteRequest
): Promise<TerminalWriteResult> {
  return getTerminalApi().write(request);
}

/**
 * Resize a terminal session
 */
export async function resizeTerminal(
  request: TerminalResizeRequest
): Promise<TerminalResizeResult> {
  return getTerminalApi().resize(request);
}

/**
 * Close a terminal session
 */
export async function closeTerminalSession(
  request: TerminalCloseRequest
): Promise<TerminalCloseResult> {
  return getTerminalApi().close(request);
}

/**
 * Subscribe to terminal output events
 * Returns a cleanup function to unsubscribe
 */
export function onTerminalOutput(
  callback: (event: TerminalOutputEvent) => void
): () => void {
  return getTerminalApi().onOutput(callback);
}

/**
 * Subscribe to terminal exit events
 * Returns a cleanup function to unsubscribe
 */
export function onTerminalExit(
  callback: (event: TerminalExitEvent) => void
): () => void {
  return getTerminalApi().onExit(callback);
}
